import { Box, Collapse, Flex, Grid, Text } from '@chakra-ui/react'
import { FC, useEffect, useMemo } from 'react'

import useKpiRupture from '@hook/kpi/use-rupture'
import useFilters from '@hook/shared/use-filters'
import useLayout from '@hook/shared/use-layout'
import useSessions from '@hook/shared/use-sessions'

import Layout from '@component/Layout'
import LayoutEmpty from '@component/Layout/Empty'
import LayoutFilters from '@component/Layout/Filters'
import LayoutLoader from '@component/Layout/Loader'
import TableGrid from '@component/Layout/TableGrid'
import LayoutTools from '@component/Layout/Tools'
import KpiRuptureGraphic from '@component/Modules/Kpi/Graphics/KpiRuptureGraphic'
import OpenOrCloseChartButton from '@component/Modules/Kpi/Graphics/OpenOrCloseButton'
import KpiModal from '@component/Modules/Kpi/Modal'

import { scalesHeightKpiTable } from '@helper/kpi/logistic/scalesHeightKpiTable'
import { disableActionsByDefault } from '@helper/kpi/rupture/disableActionsByDefault'
import { disableActionsByDefaultModal } from '@helper/kpi/rupture/disableActionsByDefaultModal'
import { validateAppVersion } from '@helper/util/storangeClear'
import generic from '@service/generic'

const KpiRupture: FC = () => {
  const { hasSessions } = useSessions()
  const { getFilters } = useFilters()
  const { menuToggle } = useLayout()
  const {
    data,
    query,
    params,
    headers,
    actions,
    filters,
    loading,
    filename,
    fetching,
    setFilters,
    currentyDate,
    filtersList,
    exportKpiData,
    tableInteraction,
    getData,
    setQuery,
    setFilename,
    clearAllFilters,
    handleUpdateParams,

    dataModal,
    isModalOpen,
    actionsModal,
    handleLocalParams,
    loadingModal,
    headersModal,
    isModalConfig,
    isLoadingExport,
    kpiModalTableStructure,
    onModalClose,
    onModalChange,
    exportKpiDataModal,
    updateIsModalConfig,

    pagination,
    handlePageChange,

    paginationMain,
    handlePageChangeMain,

    isGraphic,
    graphicValues,
    loadingGraphic,
    isShowContentChart,
    setIsGraphic,
    updateIsGraphic
  } = useKpiRupture()

  const height = useMemo(
    () =>
      menuToggle
        ? hasSessions
          ? 'calc(100vh - 30px)'
          : '100vh'
        : hasSessions
        ? 'calc(100vh - 160px)'
        : 'calc(100vh - 142px)',
    [menuToggle, hasSessions]
  )

  const tableHeight = useMemo(
    () => scalesHeightKpiTable({ menuToggle, isGraphic, hasSessions }),
    [menuToggle, isGraphic, hasSessions]
  )

  useEffect(() => {
    generic
      .getMaintence('manutencao')
      .then(({ data: { data } }) => {
        validateAppVersion(data.version)

        if (data.valor === 'true') {
          window.location.href = '/manutencao'
        } else {
          handleLocalParams()

          data?.length === 0 && getData()

          getFilters({
            companies: true,
            centers: true,
            categoryManagers: true,
            productManagers: true,
            centerTypes: true,
            worlds: true,
            compactCategories: true,
            categories: true,
            inventoryStatus: true,
            abcCurves: true,
            providersForDistribution: true,
            subCategories: true
          })
        }
      })
      .catch(() => {})

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Layout title="KPI Ruptura">
      <Grid
        w="100vw"
        bg="white"
        h={height}
        templateColumns={!filters ? '100vw' : '260px calc(100vw - 260px)'}
        isDisabled={loading || fetching || data.length === 0}
      >
        {filters && (
          <LayoutFilters
            filters={filtersList}
            defaultDate={currentyDate}
            values={params}
            onChange={handleUpdateParams}
            loading={loading}
            canCancel={fetching}
            onFilter={getData}
            onClearAllFilters={clearAllFilters}
          />
        )}
        <Box
          overflow={
            isShowContentChart && graphicValues.ruptura?.length > 0
              ? 'auto'
              : null
          }
          maxH={
            isShowContentChart && graphicValues.ruptura?.length > 0
              ? 'calc(100vh - 136px)'
              : null
          }
        >
          <LayoutTools
            title={filename}
            filters={filters}
            actions={actions}
            query={query}
            onClick={exportKpiData}
            onSearchChange={setQuery}
            onTitleChange={setFilename}
            toggleFilters={setFilters.toggle}
            isDisabled={loading || fetching || data.length === 0}
            disableActionsByDefault={disableActionsByDefault}
          />
          {loading ? (
            <LayoutLoader
              title="Recuperando informações..."
              message="Aguarde enquanto buscamos os registros no banco de dados"
            />
          ) : data?.length === 0 ? (
            <LayoutEmpty
              title="Nada encontrado!"
              message="Filtre sua busca ao lado para obter informações"
            />
          ) : (
            <>
              <Flex
                justifyContent="space-between"
                alignItems="center"
                paddingRight={4}
                paddingLeft={3}
                paddingY={1}
              >
                <Text fontSize={14}>
                  Quantidade de Registros: {paginationMain.totalRows}
                </Text>

                {isShowContentChart && graphicValues.ruptura?.length > 0 && (
                  <OpenOrCloseChartButton
                    openText="Abrir gráfico"
                    closeText="Fechar gráfico"
                    graphicValue={graphicValues.ruptura}
                    isGraphic={isGraphic}
                    setIsGraphic={setIsGraphic}
                  />
                )}
              </Flex>

              {isShowContentChart && (
                <Collapse
                  in={
                    loadingGraphic ||
                    (!loadingGraphic &&
                      graphicValues.ruptura?.length > 0 &&
                      isGraphic)
                  }
                  unmountOnExit={true}
                >
                  {loadingGraphic ? (
                    <LayoutLoader
                      type="rupture"
                      title="Recuperando informações..."
                      message="Aguarde enquanto buscamos os registros no banco de dados"
                    />
                  ) : graphicValues.ruptura?.length === 0 ? (
                    <LayoutEmpty
                      type="rupture"
                      title="Nada encontrado!"
                      message="Filtre sua busca ao lado para obter informações"
                    />
                  ) : (
                    <>
                      <KpiRuptureGraphic data={graphicValues} />
                      <Box
                        paddingBottom={3.5}
                        boxShadow="inset 0px 1px 5px 2px #dcdcdc"
                      />
                    </>
                  )}
                </Collapse>
              )}

              <TableGrid
                id="rupture-table"
                entity="kpi-rupture"
                data={data}
                query={query}
                headers={headers}
                loading={fetching}
                itsSortable={true}
                height={tableHeight}
                actions={tableInteraction}
                updateIsGraphic={updateIsGraphic}
                pagination={paginationMain}
                udatePageChange={handlePageChangeMain}
                isPagedBack={true}
                isPaged={false}
                isOverflowDisabled={true}
              />

              <KpiModal
                id="rupture-table-modal"
                entity="kpi-rupture-modal"
                data={dataModal}
                isOpen={isModalOpen}
                onClose={onModalClose}
                headers={headersModal}
                query={query}
                onClickExport={exportKpiDataModal}
                onChange={onModalChange}
                itsSortable={true}
                loadingModal={loadingModal}
                actions={actionsModal}
                isModalConfig={isModalConfig}
                updateIsModalConfig={updateIsModalConfig}
                kpiModalTableStructure={kpiModalTableStructure}
                isDisabled={disableActionsByDefaultModal}
                isLoadingExport={isLoadingExport}
                pagination={pagination}
                udatePageChange={handlePageChange}
                isPagedBack={true}
                isPaged={false}
                isExport
              />
            </>
          )}
        </Box>
      </Grid>
    </Layout>
  )
}

export default KpiRupture
